import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createIntegrationChannel,
  deleteIntegrationChannel,
  getIntegrationChannels,
  testIntegrationChannel,
  updateIntegrationChannel,
} from 'services/apis/admin'
import { Button, PlainTable } from 'stories/components'
import { confirm } from 'utils'

import { AddSlackTeamsChannelModal } from './AddSlackTeamsChannelModal'
import { EVENT_OPTIONS, IIntegrationChannel, PLATFORM_OPTIONS } from './type'

export const SlackTeamsChannels = () => {
  const [action, setAction] = useState('')
  const [channels, setChannels] = useState<IIntegrationChannel[]>([])
  const [editing, setEditing] = useState<IIntegrationChannel | null | undefined>(undefined)

  const fetchData = async () => {
    setAction('get')
    try {
      const data = await getIntegrationChannels()
      setChannels(Array.isArray(data) ? data : [])
    } finally {
      setAction('')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const onAdd = () => setEditing(null)
  const onEdit = (channel: IIntegrationChannel) => setEditing(channel)

  const onDelete = async (channel: IIntegrationChannel) => {
    const result = await confirm(`Delete channel "${channel.name}"?`)
    if (!result) return
    setAction('delete')
    try {
      await deleteIntegrationChannel(channel.id)
      await fetchData()
    } finally {
      setAction('')
    }
  }

  const onTest = async (channel: IIntegrationChannel) => {
    setAction(`test-${channel.id}`)
    try {
      await testIntegrationChannel(channel.id)
      toast(`Test message sent to "${channel.name}"`, { type: 'success' })
    } finally {
      setAction('')
    }
  }

  const onCloseModal = async (payload: Partial<IIntegrationChannel> | null) => {
    if (!payload) {
      setEditing(undefined)
      return
    }
    setAction('setting')
    try {
      if (editing && editing.id) await updateIntegrationChannel(editing.id, payload)
      else await createIntegrationChannel(payload)
      setEditing(undefined)
      await fetchData()
    } finally {
      setAction('')
    }
  }

  const tableData = channels.map((channel) => [
    channel.name,
    PLATFORM_OPTIONS[channel.platform] || channel.platform,
    <span className="whitespace-normal">
      {(channel.events || []).map((e) => EVENT_OPTIONS[e] || e).join(', ') || '-'}
    </span>,
    <span className={channel.enabled ? 'text-shade-blue' : 'text-gray-400'}>{channel.enabled ? 'Yes' : 'No'}</span>,
    <div className="flex items-center gap-2">
      <Button size="sm" outline loading={action === `test-${channel.id}`} onClick={() => onTest(channel)}>
        Send test
      </Button>
      <span className="btn-icon" onClick={() => onEdit(channel)}>
        <PencilSquareIcon className="w-4 h-4" />
      </span>
      <span className="btn-icon" onClick={() => onDelete(channel)}>
        <TrashIcon className="w-4 h-4 text-red-500" />
      </span>
    </div>,
  ])

  return (
    <div className="relative">
      <LayoutLoading show={action !== '' && !action.startsWith('test-')} />
      <div className="flex justify-between items-center pb-5">
        <h1 className="text-2xl font-bold">Slack / Teams Channels</h1>
        <Button onClick={onAdd}>Add</Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Post loan status, servicing status, task, and note-mention events to Slack or Microsoft Teams via an incoming
        webhook. Add a channel, choose which events it receives, then use Send test to confirm delivery.
      </p>

      <div className="overflow-auto sm:rounded-lg">
        <PlainTable
          thClass="!px-4 !py-2"
          tdClass="px-4 py-2 align-top"
          header={['Name', 'Platform', 'Events', 'Enabled', 'Action']}
          data={tableData}
        />
      </div>

      {editing !== undefined && <AddSlackTeamsChannelModal data={editing} onClose={onCloseModal} />}
    </div>
  )
}
